/** Shared post-login bootstrap. It never enumerates the user directory. */
import { normalizePermissions } from './permissions.js';
import { resolveBackendBase } from './backendBase.js';

function previousPresentation(userId, username) {
    try {
        const previous = JSON.parse(localStorage.getItem('user') || 'null');
        const expectedId = String(userId || '').trim();
        const expectedUsername = String(username || '').trim();
        const matchesId = expectedId && previous && String(previous.id || '') === expectedId;
        // Username is a migration bridge only when an old response genuinely
        // lacks an immutable ID.  It must not carry a prior user's display data
        // into a reused login name.
        const matchesLegacyUsername = !expectedId && expectedUsername && previous &&
            String(previous.username || '').trim() === expectedUsername;
        return matchesId || matchesLegacyUsername ? previous : null;
    }
    catch {
        return null;
    }
}

async function selfIdentity(token) {
    if (!token)
        return null;
    try {
        const response = await fetch(`${resolveBackendBase()}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'include',
        });
        return response.ok ? await response.json() : null;
    }
    catch {
        return null;
    }
}

export async function establishSession({ username, userId, role, permissions, token }) {
    // Login/refresh claims are signed by the backend. /me is a self-only
    // confirmation, never a substitute for an authorization decision.
    const verified = await selfIdentity(token);
    const resolvedUsername = String(verified?.username || username || '').trim();
    const resolvedUserId = String(verified?.user_id || userId || '').trim();
    if (!resolvedUsername || !resolvedUserId)
        throw new Error('لم تُثبت هوية الحساب بشكل كامل؛ سجّل الدخول مجدداً');
    const resolvedRole = verified?.role || role || 'technician';
    const resolvedPermissions = normalizePermissions(verified?.permissions || permissions, resolvedRole);
    const previous = previousPresentation(resolvedUserId, resolvedUsername);
    const appUser = {
        id: resolvedUserId,
        username: resolvedUsername,
        name: previous?.name || resolvedUsername,
        phone: previous?.phone || '',
        email: previous?.email || '',
        role: resolvedRole,
        permissions: resolvedPermissions,
        isActive: true,
        guidanceEnabled: previous?.guidanceEnabled !== false,
    };
    const session = {
        id: appUser.id,
        username: appUser.username,
        name: appUser.name,
        phone: appUser.phone,
        email: appUser.email,
        role: appUser.role,
        permissions: appUser.permissions,
        guidanceEnabled: appUser.guidanceEnabled,
        loginTime: new Date().toISOString(),
    };
    localStorage.setItem('session', JSON.stringify(session));
    localStorage.setItem('user', JSON.stringify(appUser));
    // Remove the legacy script-readable cookie. It is not an authentication
    // source and must not be used to restore a privileged-looking UI state.
    try {
        document.cookie = 'session=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/';
    }
    catch {
        // Browser cookie restrictions do not affect the httpOnly auth cookies.
    }
    window.dispatchEvent(new Event('sessionUpdated'));
    return session;
}
